import React, { useDeferredValue, useEffect, useMemo, useState } from "react";
import {
	Alert,
	Autocomplete,
	Box,
	Button,
	Checkbox,
	Chip,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Divider,
	ListItemText,
	MenuItem,
	Paper,
	Stack,
	TextField,
	Typography,
	ToggleButton,
	ToggleButtonGroup,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import dayjs from "dayjs";
import StorefrontIcon from "@mui/icons-material/Storefront";
import BusinessIcon from "@mui/icons-material/Business";
import AddBusinessIcon from "@mui/icons-material/AddBusiness";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import { AuthState } from "context/AuthContext";
import { GlobalState } from "context/GlobalContext";
import { pythonVmInstance } from "utils/AxiosInstances/RareboxPythonInstance";
import { roleAccess } from "../EboPaymentWorking";


const getOptionLabel = (option) =>
	option?.name || option?.label || option?.franchise_name || option?.store_name || option?.storeName || "";

const getOptionKey = (option, index) => option?.id || option?.value || option?.code || option?.key || `${getOptionLabel(option)}-${index}`;

const getStoreKey = (store) =>
	String(store?.store_code ?? store?.code ?? store?.short_code ?? store?.id ?? store?._id ?? getOptionLabel(store)).trim();

const getStoreStatus = (store) => String(store?.store_status || "enabled").toLowerCase();
const toInputString = (value) => (value === null || value === undefined ? "" : String(value));
const toDateInputValue = (value) => {
	if (!value) return "";
	const parsed = dayjs(value);
	return parsed.isValid() ? parsed.format("YYYY-MM-DD") : "";
};
const normalizeStoreRecord = (store = {}) => ({
	...store,
	admsite_code: toInputString(store?.admsite_code ?? store?.admsiteCode),
	opening_date: toDateInputValue(
		store?.opening_date ?? store?.store_opening_date ?? store?.openingDate ?? store?.storeOpeningDate
	),
	closing_date: toDateInputValue(
		store?.closing_date ?? store?.store_closing_date ?? store?.closingDate ?? store?.storeClosingDate
	),
	deposit_amount: toInputString(
		store?.deposit_amount ?? store?.depositAmount ?? store?.security_deposit ?? store?.securityDeposit
	),
	capex_amount: toInputString(store?.capex_amount ?? store?.capexAmount ?? store?.capex),
	area: toInputString(store?.area ?? store?.store_area ?? store?.storeArea),
});

const SELECT_ALL_STORE_OPTION = {
	id: "__select_all__",
	name: "Select All Stores",
	code: "ALL",
	selectAll: true,
};


const getResponseRows = (data) => {
	if (Array.isArray(data)) return data;
	if (Array.isArray(data?.data)) return data.data;
	if (Array.isArray(data?.result)) return data.result;
	if (Array.isArray(data?.results)) return data.results;
	if (Array.isArray(data?.rows)) return data.rows;
	return [];
};
const formatTableHeader = (field) =>
	String(field || "")
		.replace(/_/g, " ")
		.replace(/([a-z])([A-Z])/g, "$1 $2")
		.trim()
		.toUpperCase();
const formatTableValue = (value) => {
	if (value === null || value === undefined || value === "") return "-";
	if (typeof value === "object") return JSON.stringify(value);
	return String(value);
};
const downloadTextFile = (content, fileName, mimeType) => {
	const blob = new Blob([content], { type: mimeType });
	const downloadLink = document.createElement("a");
	downloadLink.href = window.URL.createObjectURL(blob);
	downloadLink.download = fileName;
	downloadLink.click();
	window.URL.revokeObjectURL(downloadLink.href);
};
const escapeCsvCell = (value) => {
	const text = formatTableValue(value);
	return /[",\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
};

const normalizeMappedFranchise = (row, index) => ({
	...row,
	id: row?.id ?? row?._id ?? row?.franchise_id ?? `mapped-franchise-${index + 1}`,
	name: row?.name ?? row?.franchise_name ?? row?.franchise ?? row?.label ?? "",
	franchise_name: row?.franchise_name ?? row?.name ?? row?.franchise ?? row?.label ?? "",
	code: row?.code ?? row?.franchise_code ?? row?.franchise_key ?? row?.key ?? "",
	stores: getResponseRows(row?.stores || row?.mapped_stores || row?.franchise_stores || row?.store_list || row?.storeList).map(normalizeStoreRecord),
});

const storeDetailsGridTemplate =
	"minmax(180px, 1.4fr) minmax(120px, 1fr) minmax(140px, 1fr) minmax(140px, 1fr) minmax(120px, 0.9fr) minmax(120px, 0.9fr) minmax(120px, 0.9fr)";
const storeDetailsHeaders = ["Store", "Code", "ADM Site Code", "Opening Date", "Deposit", "Capex", "Area"];
const storeDetailsColumnMinWidth = 940;
const getComparableStoreSnapshot = (store = {}) => ({
	store_status: String(store?.store_status || "enabled"),
	admsite_code: toInputString(store?.admsite_code),
	opening_date: toDateInputValue(store?.opening_date),
	closing_date: toDateInputValue(store?.closing_date),
	deposit_amount: toInputString(store?.deposit_amount),
	capex_amount: toInputString(store?.capex_amount),
	area: toInputString(store?.area),
});

const MappingStores = ({
}) => {
	const [mode, setMode] = useState("existing");
	const [cofoStore, setCofoStore] = useState([]);
	const [franchiseList, setFranchiseList] = useState([]);
	const [selectedFranchise, setSelectedFranchise] = useState(null);
	const [newFranchiseName, setNewFranchiseName] = useState("");
	const [selectedStores, setSelectedStores] = useState(() => {
		if (mode === "existing") {
			return franchiseList[0]?.stores || [];
		}
		return [];
	});
	const [isEditingExisting, setIsEditingExisting] = useState(false);
	const [lastSavedPayload, setLastSavedPayload] = useState(null);
	const [error, setError] = useState("");
	const [saving, setSaving] = useState(false);
	const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
	const [storeDetailsSearch, setStoreDetailsSearch] = useState("");
	const deferredStoreDetailsSearch = useDeferredValue(storeDetailsSearch);
	const [disableDialogStore, setDisableDialogStore] = useState(null);
	const [disableClosingDate, setDisableClosingDate] = useState("");
	const [disableDialogError, setDisableDialogError] = useState("");
	const [storesDialogOpen, setStoresDialogOpen] = useState(false);
	const [storesTableRows, setStoresTableRows] = useState([]);
	const [storesTableLoading, setStoresTableLoading] = useState(false);

	const franchiseLabel = mode === "existing" ? getOptionLabel(selectedFranchise) : newFranchiseName.trim();
	const storeCount = selectedStores.length;
	const enabledStoreCount = useMemo(
		() => selectedStores.filter((store) => getStoreStatus(store) !== "disabled").length,
		[selectedStores]
	);
	const canEditStores = mode === "new" || isEditingExisting;
	const showSelectAllOption = mode === "new" || (mode === "existing" && isEditingExisting);
	const selectedFranchiseStoreKeys = useMemo(
		() => new Set((selectedFranchise?.stores || []).map((store) => getStoreKey(store)).filter(Boolean)),
		[selectedFranchise]
	);
	const lockedSelectedStores = useMemo(() => {
		if (mode !== "existing" || !isEditingExisting) return [];
		return selectedStores.filter((store) => selectedFranchiseStoreKeys.has(getStoreKey(store)));
	}, [isEditingExisting, mode, selectedFranchiseStoreKeys, selectedStores]);
	const lockedSelectedStoreKeys = useMemo(
		() => new Set(lockedSelectedStores.map((store) => getStoreKey(store)).filter(Boolean)),
		[lockedSelectedStores]
	);

	const otherFranchiseMappedStoreKeys = useMemo(() => {
		const keys = new Set();
		franchiseList.forEach((franchise) => {
			if (mode === "existing" && String(franchise?.id) === String(selectedFranchise?.id)) return;
			(franchise?.stores || []).forEach((store) => {
				const key = getStoreKey(store);
				if (key) keys.add(key);
			});
		});
		return keys;
	}, [franchiseList, mode, selectedFranchise]);
	const selectableStores = useMemo(() => {
		const uniqueStores = new Map();
		const addStore = (store) => {
			const key = getStoreKey(store);
			if (!key) return;
			if (!uniqueStores.has(key)) {
				uniqueStores.set(key, store);
			}
		};

		if (mode === "new") {
			cofoStore.forEach((store) => {
				if (!otherFranchiseMappedStoreKeys.has(getStoreKey(store))) {
					addStore(store);
				}
			});
			return Array.from(uniqueStores.values());
		}
		if (mode === "existing") {
			(selectedFranchise?.stores || []).forEach(addStore);
			cofoStore.forEach((store) => {
				const storeKey = getStoreKey(store);
				if (selectedFranchiseStoreKeys.has(storeKey) || !otherFranchiseMappedStoreKeys.has(storeKey)) {
					addStore(store);
				}
			});
			return Array.from(uniqueStores.values());
		}
		return cofoStore;
	}, [cofoStore, mode, otherFranchiseMappedStoreKeys, selectedFranchise, selectedFranchiseStoreKeys]);
	const storePickerOptions = showSelectAllOption ? [SELECT_ALL_STORE_OPTION, ...selectableStores] : selectableStores;
	const allStoresSelected =
		selectableStores.length > 0 &&
		selectedStores.length === selectableStores.length &&
		selectableStores.every((store) =>
			selectedStores.some((selected) => getStoreKey(selected) === getStoreKey(store))
		);
	const originalSelectedStoreMap = useMemo(() => {
		const sourceStores =
			mode === "existing"
				? selectedFranchise?.stores || []
				: cofoStore.filter((store) =>
						selectedStores.some((selected) => getStoreKey(selected) === getStoreKey(store))
				  );
		return new Map(sourceStores.map((store) => [getStoreKey(store), normalizeStoreRecord(store)]));
	}, [cofoStore, mode, selectedFranchise, selectedStores]);

	const { setSnackBar, snackBar, setSnackBarOpen, setLoadingOpen } = GlobalState();
	const { user } = AuthState();

	const roleAccessData = roleAccess(user);
	const isReadOnlyUser = ["VIEWER", "EDITOR"].includes(roleAccessData?.roles);

    const handleGetCofoStores = async () => {
            try {
                setLoadingOpen(true);
                await pythonVmInstance({
                    url: `/ebo-payment-working/cofo/cofo-stores`,
                    headers: {
                        "Content-Type": "application/json",
                    },
                    params: {
                        "store_type": "cofo",
                    },
                    method: "GET",
                }).then((res) => {
                    setCofoStore(getResponseRows(res?.data).map(normalizeStoreRecord));	
                });
                setLoadingOpen(false);
            } catch (err) {
                setSnackBarOpen(true);
                setLoadingOpen(false);
                setSnackBar({
                    ...snackBar,
                    message: "Failed to fetch stores",
                    severity: "error",
                    anchorOrigin: { horizontal: "center", vertical: "top" },
                });
            }
        };

	const handleGetMappedStores = async () => {
		try {
			setLoadingOpen(true);
			const res = await pythonVmInstance({
				url: `/ebo-payment-working/cofo/mapped-stores`,
				headers: {
					"Content-Type": "application/json",
				},
				method: "GET",
			});
			const mappedFranchises = getResponseRows(res?.data).map(normalizeMappedFranchise).filter((row) => row?.franchise_name || row?.name);
			const fallbackFranchises = franchiseList.map(normalizeMappedFranchise);
			const nextFranchiseList = mappedFranchises.length ? mappedFranchises : fallbackFranchises;

			setFranchiseList(nextFranchiseList);
			setSelectedFranchise((currentSelected) => {
				if (!nextFranchiseList.length) return null;
				if (!currentSelected) return nextFranchiseList[0];
				return nextFranchiseList.find((item) => String(item.id) === String(currentSelected.id)) || nextFranchiseList[0];
			});
			setSelectedStores((currentStores) => {
				if (currentStores?.length) return currentStores;
				return (nextFranchiseList[0]?.stores || []).map(normalizeStoreRecord);
			});
			setLoadingOpen(false);
		} catch (err) {
			const fallbackFranchises = franchiseList.map(normalizeMappedFranchise);
			setFranchiseList(fallbackFranchises);
			setSelectedFranchise(fallbackFranchises[0] || null);
			setSelectedStores((fallbackFranchises[0]?.stores || []).map(normalizeStoreRecord));
			setSnackBarOpen(true);
			setLoadingOpen(false);
			setSnackBar({
				...snackBar,
				message: "Failed to fetch mapped franchises",
				severity: "error",
				anchorOrigin: { horizontal: "center", vertical: "top" },
			});
		}
	};

	const handleOpenStoresDialog = async () => {
		try {
			setStoresDialogOpen(true);
			setStoresTableLoading(true);
			const res = await pythonVmInstance({
				url: `/ebo-payment-working/cofo/mapped-stores-details`,
				headers: {
					"Content-Type": "application/json",
				},
				params: {
					store_type: "cofo",
				},
				method: "GET",
			});
			setStoresTableRows(getResponseRows(res?.data));
		} catch (fetchError) {
			setStoresTableRows([]);
			setSnackBarOpen(true);
			setSnackBar({
				...snackBar,
				message: fetchError?.response?.data?.message || "Failed to fetch stores",
				severity: "error",
				anchorOrigin: { horizontal: "center", vertical: "top" },
			});
		} finally {
			setStoresTableLoading(false);
		}
	};

	const handleExportStoresTable = () => {
		if (!storesTableRows.length || !storesTableColumns.length) return;

		const headerRow = storesTableColumns.map((column) => escapeCsvCell(formatTableHeader(column))).join(",");
		const dataRows = storesTableRows.map((row) => storesTableColumns.map((column) => escapeCsvCell(row?.[column])).join(","));
		const csvContent = [headerRow, ...dataRows].join("\n");
		downloadTextFile(csvContent, `cofo_stores_${dayjs().format("YYYYMMDD_HHmmss")}.csv`, "text/csv;charset=utf-8;");
	};

	const storePreview = useMemo(
		() =>
			selectedStores.map((store, index) => ({
				...store,
				order: index + 1,
			})),
		[selectedStores]
	);
	const popupEditableStores = useMemo(
		() => selectedStores.filter((store) => getStoreStatus(store) !== "disabled"),
		[selectedStores]
	);
	const filteredStoreDetailsRows = useMemo(() => {
		const searchTerm = deferredStoreDetailsSearch.trim().toLowerCase();

		return popupEditableStores.filter((store) => {
			const storeName = getOptionLabel(store).toLowerCase();
			const storeCode = String(store?.store_code || store?.code || store?.admsite_code || "").toLowerCase();
			const admSiteCode = String(store?.admsite_code || "").toLowerCase();

			return !searchTerm || storeName.includes(searchTerm) || storeCode.includes(searchTerm) || admSiteCode.includes(searchTerm);
		});
	}, [deferredStoreDetailsSearch, popupEditableStores]);
	const storesTableColumns = useMemo(() => {
		const fields = Array.from(new Set(storesTableRows.flatMap((row) => Object.keys(row || {}))));
		return fields;
	}, [storesTableRows]);
	const storesTableGridColumns = useMemo(
		() =>
			storesTableColumns.map((column) => ({
				field: column,
				headerName: formatTableHeader(column),
				minWidth: 120,
				flex: 1,
				sortable: false,
				headerAlign: "left",
				align: "left",
				valueFormatter: (value) => formatTableValue(value?.value ?? value),
			})),
		[storesTableColumns]
	);
	const storesTableGridRows = useMemo(
		() =>
			storesTableRows.map((row, index) => ({
				id: row?.id ?? row?._id ?? row?.store_code ?? row?.code ?? `store-row-${index + 1}`,
				...row,
			})),
		[storesTableRows]
	);

	useEffect(() => {
		handleGetCofoStores();
        handleGetMappedStores();
	}, []);

	useEffect(() => {
		if (!franchiseList.length) return;
		setFranchiseList((prev) => (prev.length ? prev : franchiseList.map(normalizeMappedFranchise)));
		setSelectedFranchise((currentSelected) => {
			if (currentSelected) return currentSelected;
			return normalizeMappedFranchise(franchiseList[0], 0);
		});
	}, [franchiseList]);

	const handleModeChange = (_, nextMode) => {
		if (!nextMode) return;
		setMode(nextMode);
		setError("");
		setLastSavedPayload(null);
		setIsEditingExisting(false);
		if (nextMode === "existing") {
			const fallbackFranchise = franchiseList[0] || null;
			setSelectedFranchise(fallbackFranchise);
			setSelectedStores((fallbackFranchise?.stores || []).map(normalizeStoreRecord));
		}
		if (nextMode === "new") {
			setSelectedStores([]);
			setIsEditingExisting(true);
		}
	};

	const handleFranchiseChange = (event) => {
		const next = franchiseList.find((item) => String(item.id) === String(event.target.value)) || null;
		setSelectedFranchise(next);
		setSelectedStores((next?.stores || []).map(normalizeStoreRecord));
		setIsEditingExisting(false);
		setError("");
		setLastSavedPayload(null);
	};

	const handleStoreChange = (_, nextValue, reason, details) => {
		const selectedOption = details?.option;
		if (selectedOption?.selectAll) {
			if (allStoresSelected) {
				setSelectedStores(mode === "existing" && isEditingExisting ? lockedSelectedStores : []);
			} else {
				setSelectedStores(selectableStores.map(normalizeStoreRecord));
			}
			setError("");
			return;
		}

		const filteredStores = (nextValue || []).filter((option) => !option?.selectAll);
		if (mode === "existing" && isEditingExisting) {
			const mergedStores = [...lockedSelectedStores.map(normalizeStoreRecord)];
			filteredStores.forEach((store) => {
				const storeKey = getStoreKey(store);
				if (!lockedSelectedStoreKeys.has(storeKey)) {
					mergedStores.push(normalizeStoreRecord(store));
				}
			});
			setSelectedStores(mergedStores);
		} else {
			setSelectedStores(filteredStores.map(normalizeStoreRecord));
		}
		setError("");
	};

	const handleStoreStatusChange = (storeToUpdate, nextStatus) => {
		const targetKey = getStoreKey(storeToUpdate);
		setSelectedStores((currentStores) =>
			currentStores.map((store) =>
				getStoreKey(store) === targetKey
					? {
						...store,
						store_status: nextStatus,
					}
					: store
			)
		);
		setError("");
	};

	const handleDisableStoreClick = (store) => {
		setDisableDialogStore(store);
		setDisableClosingDate(toDateInputValue(store?.closing_date));
		setDisableDialogError("");
		setError("");
	};

	const handleConfirmDisableStore = async () => {
		if (!disableClosingDate) {
			setDisableDialogError("Please select a closing date before disabling the store.");
			return;
		}

		const targetKey = getStoreKey(disableDialogStore);
		const updatedStores = selectedStores.map((store) =>
				getStoreKey(store) === targetKey
					? {
						...store,
						store_status: "disabled",
						closing_date: disableClosingDate,
					}
					: store
		);
		setSelectedStores(updatedStores);
		setDisableDialogStore(null);
		setDisableClosingDate("");
		setDisableDialogError("");
		setError("");
		await submitMapping(updatedStores, { closeDetailsDialog: false });
	};

	const handleStoreFieldChange = (storeToUpdate, field, value) => {
		const targetKey = getStoreKey(storeToUpdate);
		setSelectedStores((currentStores) =>
			currentStores.map((store) =>
				getStoreKey(store) === targetKey
					? {
						...store,
						[field]: value,
					}
					: store
			)
		);
		setError("");
	};

	const handleSave = () => {
		const resolvedFranchiseName = mode === "existing" ? getOptionLabel(selectedFranchise) : newFranchiseName.trim();
		if (!resolvedFranchiseName) {
			setError(mode === "existing" ? "Please select an existing franchise." : "Please enter a new franchise name.");
			return;
		}
		if (!selectedStores.length) {
			setError("Please select at least one store.");
			return;
		}

		setStoreDetailsSearch("");
		setDetailsDialogOpen(true);
	};

	const submitMapping = async (storesToSave, options = {}) => {
		const resolvedFranchiseName = mode === "existing" ? getOptionLabel(selectedFranchise) : newFranchiseName.trim();
		if (!resolvedFranchiseName) {
			setError(mode === "existing" ? "Please select an existing franchise." : "Please enter a new franchise name.");
			return;
		}

		const payload = {
			mode,
			franchise:
				mode === "existing"
					? selectedFranchise
					: { name: resolvedFranchiseName },
			stores: storesToSave,
		};

		const requestPayload = {
			store_type: "cofo",
			email: user?.email || "",
			franchise_name: resolvedFranchiseName,
			stores: storesToSave.map((store) => {
				const originalStore = originalSelectedStoreMap.get(getStoreKey(store));
				const currentSnapshot = getComparableStoreSnapshot({
					...store,
					store_status: store?.store_status || "enabled",
				});
				const originalSnapshot = originalStore ? getComparableStoreSnapshot(originalStore) : null;
				const edited = !originalSnapshot || Object.keys(currentSnapshot).some((key) => currentSnapshot[key] !== originalSnapshot[key]);

				return {
					...store,
					store_code: store?.store_code || "",
					admsite_code: store?.admsite_code || "",
					store_name: getOptionLabel(store),
					store_status: store?.store_status || "enabled",
					opening_date: store?.opening_date || null,
					closing_date: store?.closing_date || null,
					deposit_amount: store?.deposit_amount === "" ? null : store?.deposit_amount,
					capex_amount: store?.capex_amount === "" ? null : store?.capex_amount,
					area: store?.area === "" ? null : store?.area,
					edited,
				};
			}),
		};

		try {
			setSaving(true);
			setError("");
			if (options.closeDetailsDialog !== false) {
				setDetailsDialogOpen(false);
			}
			setLoadingOpen(true);
			const response = await pythonVmInstance({
				url: `/ebo-payment-working/cofo/franchise-mapping`,
				headers: {
					"Content-Type": "application/json",
				},
				method: mode === "existing" ? "PUT" : "POST",
				data: requestPayload,
			});
			const savedMapping = response?.data?.data || response?.data?.result || response?.data || {};
			const nextFranchiseRecord = {
				...(mode === "existing" ? selectedFranchise : {}),
				...(savedMapping && typeof savedMapping === "object" ? savedMapping : {}),
				id:
					savedMapping?.id ??
					savedMapping?._id ??
					(mode === "existing" ? selectedFranchise?.id : `${resolvedFranchiseName}-${Date.now()}`),
				name:
					savedMapping?.name ??
					savedMapping?.franchise_name ??
					savedMapping?.franchise ??
					resolvedFranchiseName,
				code:
					savedMapping?.code ??
					savedMapping?.franchise_code ??
					savedMapping?.franchise_key ??
					(mode === "existing" ? selectedFranchise?.code : resolvedFranchiseName),
				stores: storesToSave,
			};

			setFranchiseList((prev) => {
				if (mode === "existing") {
					return prev.map((item) =>
						String(item.id) === String(selectedFranchise?.id) ? nextFranchiseRecord : item
					);
				}
				return [nextFranchiseRecord, ...prev];
			});
			setSelectedFranchise(nextFranchiseRecord);
			setLastSavedPayload(payload);
			setSnackBarOpen(true);
			setSnackBar({
				...snackBar,
				message: mode === "existing" ? "Franchise mapping updated successfully" : "Franchise mapping saved successfully",
				severity: "success",
				anchorOrigin: { horizontal: "center", vertical: "top" },
			});
			await handleGetMappedStores();
			if (mode === "existing") {
				setIsEditingExisting(false);
			} else {
				setMode("existing");
				setNewFranchiseName("");
				setIsEditingExisting(false);
			}
		} catch (saveError) {
			if (options.reopenDetailsDialogOnError) {
				setDetailsDialogOpen(true);
			}
			setError(saveError?.response?.data?.message || saveError?.message || "Failed to save franchise-store mapping.");
		} finally {
			setSaving(false);
			setLoadingOpen(false);
		}
	};

	const handleConfirmSave = async () => {
		await submitMapping(selectedStores, { reopenDetailsDialogOnError: true });
	};

	const handleReset = () => {
		setMode(mode);
		const fallbackFranchises = franchiseList.length ? franchiseList : franchiseList.map(normalizeMappedFranchise);
		setFranchiseList(fallbackFranchises);
		setSelectedFranchise(fallbackFranchises[0] || null);
		setNewFranchiseName("");
		setSelectedStores(mode === "existing" ? (fallbackFranchises[0]?.stores || []).map(normalizeStoreRecord) : []);
		setIsEditingExisting(false);
		setDetailsDialogOpen(false);
		setStoreDetailsSearch("");
		setLastSavedPayload(null);
		setError("");
	};

	return (
		<Paper
			variant="outlined"
			sx={{
				p: 1.5,
				borderRadius: 2,
				background:
					"linear-gradient(180deg, rgba(255,255,255,0.98) 0%, rgba(248,250,252,0.96) 100%)",
			}}>
			<Stack spacing={1.5}>
				<Box
					sx={{
						display: "flex",
						justifyContent: "space-between",
						alignItems: { xs: "flex-start", sm: "center" },
						gap: 1,
						flexWrap: "wrap",
					}}>
					<Box>
						<Typography variant="subtitle1" fontWeight={800}>
							Map Stores Under a Franchise
						</Typography>
						<Typography variant="body2" color="text.secondary" sx={{ fontSize: 12.5 }}>
							Use this screen when you only have store data and need to attach those stores to an existing franchise or create a new one first.
						</Typography>
					</Box>
					<Button variant="outlined" size="small" onClick={handleOpenStoresDialog} sx={{ minHeight: 32, px: 1.5, fontSize: 12 }}>
						Stores
					</Button>
				</Box>

				<ToggleButtonGroup
					value={mode}
					exclusive
					onChange={handleModeChange}
					size="small"
					sx={{ alignSelf: "flex-start", "& .MuiToggleButton-root": { minHeight: 34, py: 0.5 } }}>
					<ToggleButton value="existing" sx={{ px: 1.5, fontSize: 12 }}>
						<Box display="flex" alignItems="center" gap={1}>
							<BusinessIcon sx={{ fontSize: 16 }} />
							Existing Franchise
						</Box>
					</ToggleButton>
					<ToggleButton value="new" sx={{ px: 1.5, fontSize: 12 }} disabled={isReadOnlyUser}>
						<Box display="flex" alignItems="center" gap={1}>
							<AddBusinessIcon sx={{ fontSize: 16 }} />
							Create New Franchise
						</Box>
					</ToggleButton>
				</ToggleButtonGroup>

				<Box
					sx={{
						display: "grid",
						gridTemplateColumns: { xs: "1fr", lg: "1.05fr 0.95fr" },
						gap: 2,
						alignItems: "start",
					}}>
					<Stack spacing={2}>
						<Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
							<Stack spacing={1.25}>
								{mode === "existing" ? (
									<Box
										sx={{
											p: 1.5,
											borderRadius: 2,
											bgcolor: "rgba(59, 130, 246, 0.06)",
											border: "1px solid",
											borderColor: "rgba(59, 130, 246, 0.18)",
										}}>
										<Typography variant="overline" color="primary" fontWeight={800} sx={{ lineHeight: 1.1 }}>
											Existing Franchise Review
										</Typography>
										<Typography variant="subtitle1" fontWeight={800}>
											{franchiseLabel || "Select a franchise"}
										</Typography>
										<Typography variant="body2" color="text.secondary" sx={{ fontSize: 12.5 }}>
											Review the mapped stores first. Use edit mode only when you want to change the store list.
										</Typography>
										<Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mt: 1.5 }}>
											<Chip label={`${storeCount} store${storeCount === 1 ? "" : "s"} mapped`} size="small" />
											<Chip label={isEditingExisting ? "Edit mode on" : "View mode"} size="small" color={isEditingExisting ? "warning" : "default"} />
										</Box>
									</Box>
								) : (
									<Box
										sx={{
											p: 1.5,
											borderRadius: 2,
											bgcolor: "rgba(16, 185, 129, 0.06)",
											border: "1px solid",
											borderColor: "rgba(16, 185, 129, 0.18)",
										}}>
										<Typography variant="overline" color="success.main" fontWeight={800} sx={{ lineHeight: 1.1 }}>
											Create Franchise Setup
										</Typography>
										<Typography variant="subtitle1" fontWeight={800} sx={{ mb: 0.75 }}>
											Add only the franchise name before mapping stores
										</Typography>
										<Stack spacing={1.25}>
											<TextField
												fullWidth
												size="small"
												label="Franchise Name"
												placeholder="Enter franchise name"
												value={newFranchiseName}
												onChange={(event) => {
													setNewFranchiseName(event.target.value);
													setError("");
												}}
												sx={{ "& .MuiInputBase-input": { fontSize: 13 } }}
											/>
										</Stack>
									</Box>
								)}

								{mode === "existing" ? (
									<>
										<TextField
											select
											fullWidth
											size="small"
											label="Existing Franchise"
											value={selectedFranchise?.id || ""}
											onChange={handleFranchiseChange}
											helperText="Choose a franchise that already exists in the system."
											sx={{ "& .MuiInputBase-input": { fontSize: 13 }, "& .MuiFormHelperText-root": { fontSize: 11 } }}
										>
											{franchiseList.length > 0 ? (
												franchiseList.map((franchise, index) => (
													<MenuItem key={getOptionKey(franchise, index)} value={franchise.id}>
														{franchise.franchise_name || franchise.name}
													</MenuItem>
												))
											) : (
												<MenuItem value="" disabled>
													No franchises available
												</MenuItem>
											)}
										</TextField>
										<Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
											<Button
												variant="outlined"
												onClick={() => setIsEditingExisting(true)}
												disabled={!selectedFranchise || saving || isEditingExisting || isReadOnlyUser}
												startIcon={<EditOutlinedIcon />}
												size="small"
												sx={{ alignSelf: "flex-start", minHeight: 32, px: 1.5, fontSize: 12 }}>
												Edit
											</Button>
										</Box>
									</>
								) : null}
							</Stack>
						</Paper>

						{ canEditStores && <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
							<Stack spacing={1.25}>
								<Typography variant="subtitle2" fontWeight={700}>
									{mode === "existing" ? (isEditingExisting && "Edit Stores") : "Assign Stores"}
								</Typography>
								{canEditStores ? (
									<Autocomplete
										multiple
										options={storePickerOptions}
										value={selectedStores}
										onChange={handleStoreChange}
										disableCloseOnSelect
										limitTags={2}
										getOptionLabel={getOptionLabel}
										isOptionEqualToValue={(option, value) =>
											Boolean(option?.selectAll && value?.selectAll) ||
											String(option?.store_code ?? option?.id ?? option?.code) === String(value?.store_code ?? value?.id ?? value?.code)
										}
										renderOption={(props, option, { selected }) => (
											(() => {
												const isLockedStore = !option?.selectAll && lockedSelectedStoreKeys.has(getStoreKey(option));
												const currentSelectedStore = selectedStores.find(
													(store) => getStoreKey(store) === getStoreKey(option)
												);
												const storeStatus = getStoreStatus(currentSelectedStore || option);
												return (
													<li
														{...props}
														key={getOptionKey(option, props?.id)}
														style={{
															...props.style,
															paddingTop: 4,
															paddingBottom: 4,
															minHeight: 34,
														}}>
														<Checkbox
															sx={{ mr: 0.75, p: 0.35 }}
															checked={option?.selectAll ? allStoresSelected : selected}
															disabled={isLockedStore}
														/>
														<ListItemText
															primary={getOptionLabel(option)}
															secondary={
																option?.selectAll
																	? "Select every store at once"
																	: isLockedStore
																	? `Already mapped to this franchise • ${storeStatus}`
																	: option?.code || option?.store_code || ""
															}
															primaryTypographyProps={{ fontSize: 13, lineHeight: 1.15 }}
															secondaryTypographyProps={{ fontSize: 11, lineHeight: 1.1 }}
															sx={{ my: 0 }}
														/>
														{isLockedStore ? (
															<Button
																size="small"
																variant={storeStatus === "disabled" ? "outlined" : "contained"}
																		color={storeStatus === "disabled" ? "inherit" : "warning"}
																		onClick={(event) => {
																			event.preventDefault();
																			event.stopPropagation();
																			handleDisableStoreClick(option);
																		}}
																		disabled={storeStatus === "disabled"}
																		sx={{ ml: 1, minWidth: 76, fontSize: 11, px: 1 }}>
																{storeStatus === "disabled" ? "Disabled" : "Disable"}
															</Button>
														) : null}
													</li>
												);
											})()
										)}
										renderTags={(value, getTagProps) => {
											const visibleTags = value.slice(0, 2);
											const hiddenCount = value.length - visibleTags.length;
											return (
												<Box sx={{ display: "flex", flexWrap: "nowrap", gap: 0.75, overflow: "hidden" }}>
													{visibleTags.map((option, index) => {
														const tagProps = getTagProps({ index });
														const isLockedStore = lockedSelectedStoreKeys.has(getStoreKey(option));
														return (
															<Chip
																{...tagProps}
																key={getOptionKey(option, index)}
																label={
																	lockedSelectedStoreKeys.has(getStoreKey(option))
																		? `${getOptionLabel(option)} (${getStoreStatus(option)})`
																		: getOptionLabel(option)
																}
																size="small"
																icon={<StorefrontIcon />}
																onDelete={isLockedStore ? undefined : tagProps.onDelete}
																sx={{ maxWidth: 170 }}
																variant={isLockedStore ? "outlined" : "filled"}
															/>
														);
													})}
													{hiddenCount > 0 ? (
														<Chip
															label={`+${hiddenCount} more`}
															size="small"
															variant="outlined"
															sx={{ maxWidth: 110 }}
														/>
													) : null}
												</Box>
											);
										}}
										renderInput={(params) => (
											<TextField
												{...params}
												label="Stores"
												placeholder="Search stores"
												size="small"
												helperText={
													mode === "existing" && isEditingExisting
														? "Existing mapped stores stay selected. You can add new stores from the list."
														: ""
												}
											/>
										)}
										sx={{
											"& .MuiInputBase-root": {
												minHeight: 38,
												alignItems: "flex-start",
												pt: 0.25,
											},
											"& .MuiAutocomplete-input": {
												minWidth: 90,
												fontSize: 13,
											},
										}}
										ListboxProps={{
											style: {
												maxHeight: 280,
												overflow: "auto",
											},
										}}
									/>
								) : (
									""
								)}
							</Stack>
						</Paper>}
					</Stack>

					<Paper
						variant="outlined"
						sx={{
							p: 1.5,
							borderRadius: 2,
							minHeight: 220,
							bgcolor: "rgba(17, 24, 39, 0.03)",
						}}>
						<Stack spacing={1.25}>
							<Box display="flex" alignItems="center" gap={1}>
								<CheckCircleIcon color="success" />
								<Typography variant="subtitle2" fontWeight={700}>
									Mapping Preview
								</Typography>
							</Box>

							{/* <Box>
								<Typography variant="body2" color="text.secondary">
									Franchise Type
								</Typography>
								<Typography variant="body2" fontWeight={600}>
									{mode === "existing" ? "Existing Franchise" : "New Franchise"}
								</Typography>
							</Box> */}

							<Box>
								<Typography variant="body2" color="text.secondary">
									Franchise Name
								</Typography>
								<Typography variant="body2" fontWeight={600}>
									{franchiseLabel || "Not selected"}
								</Typography>
							</Box>

							{mode === "existing" ? (
								<>
									<Box
										sx={{
											p: 1.25,
											borderRadius: 1.5,
											bgcolor: "background.paper",
											border: "1px solid",
											borderColor: "divider",
										}}>
										<Typography variant="body2" color="text.secondary">
											Current Store Count
										</Typography>
										<Typography variant="h6" fontWeight={800}>
											{enabledStoreCount}
										</Typography>
										<Typography variant="body2" color="text.secondary">
											{franchiseLabel || "Selected franchise"} is currently mapped to these enabled stores.
										</Typography>
									</Box>

									<Divider />

									<Box>
										<Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
											Store Names
										</Typography>
										<Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
											{storePreview.map((store) => (
												<Chip
													key={store.id || store.code || store.name}
													label={
														getStoreStatus(store) === "disabled"
															? `${getOptionLabel(store)} (Disabled)`
															: getOptionLabel(store)
													}
													size="small"
													color={getStoreStatus(store) === "disabled" ? "default" : "primary"}
													variant={getStoreStatus(store) === "disabled" ? "filled" : "outlined"}
												/>
											))}
										</Box>
									</Box>
								</>
							) : (
								<>
									<Box
										sx={{
											p: 1.25,
											borderRadius: 1.5,
											bgcolor: "background.paper",
											border: "1px solid",
											borderColor: "divider",
										}}>
										<Typography variant="body2" color="text.secondary">
											New Franchise Info
										</Typography>
										<Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mt: 1 }}>
											<Chip label={`${storeCount} store${storeCount === 1 ? "" : "s"}`} size="small" color="success" variant="outlined" />
										</Box>
									</Box>

									<Divider />

									<Box>
										<Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
											Selected Store Names
										</Typography>
										<Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
											{storePreview.length > 0 ? (
												storePreview.map((store) => (
													<Chip
														key={store.id || store.code || store.name}
														label={
															getStoreStatus(store) === "disabled"
																? `${getOptionLabel(store)} (Disabled)`
																: getOptionLabel(store)
														}
														size="small"
														color={getStoreStatus(store) === "disabled" ? "default" : "primary"}
														variant={getStoreStatus(store) === "disabled" ? "filled" : "outlined"}
													/>
												))
											) : (
												<Typography variant="body2" color="text.secondary">
													Pick stores to see the summary here.
												</Typography>
											)}
										</Box>
									</Box>
								</>
							)}
						</Stack>
					</Paper>
				</Box>

				{error ? <Alert severity="error">{error}</Alert> : null}
				{lastSavedPayload ? (
					<Alert severity="success">
						Mapping ready. {lastSavedPayload.stores.length} store(s) prepared for {franchiseLabel}.
					</Alert>
				) : null}

				<Box display="flex" justifyContent="flex-end" gap={1}>
					{mode === "new" || isEditingExisting ? (
						<Button variant="text" onClick={handleReset} disabled={saving || isReadOnlyUser} size="small" sx={{ minHeight: 32, px: 1.25, fontSize: 12 }}>
							Reset
						</Button>
					) : null}
					{mode === "new" || isEditingExisting ? (
						<Button variant="contained" onClick={handleSave} disabled={saving || isReadOnlyUser} size="small" sx={{ minHeight: 32, px: 1.5, fontSize: 12 }}>
							{saving ? "Saving..." : "Save Mapping"}
						</Button>
					) : null}
				</Box>

				<Dialog
					open={detailsDialogOpen}
					onClose={saving ? undefined : () => setDetailsDialogOpen(false)}
					fullWidth
					maxWidth="xl"
					PaperProps={{
						sx: {
							height: "80vh",
							maxHeight: "80vh",
						},
					}}>
					<DialogTitle>Store Details</DialogTitle>
					<DialogContent
						dividers
						sx={{
							px: 2,
							py: 1.5,
							display: "flex",
							flexDirection: "column",
							overflow: "hidden",
						}}>
						<Stack spacing={1.25} sx={{ pt: 0.5, minHeight: 0, flex: 1 }}>
							<Typography variant="body2" color="text.secondary">
								Fill the required details for the selected stores, then click save to complete the mapping.
							</Typography>
							<TextField
								size="small"
								placeholder="Search by store name, code, or ADM site code"
								value={storeDetailsSearch}
								onChange={(event) => setStoreDetailsSearch(event.target.value)}
								sx={{
									maxWidth: 420,
									"& .MuiInputBase-root": {
										height: 36,
										alignItems: "center",
									},
									"& .MuiInputBase-input": {
										fontSize: 12,
										py: 1,
									},
								}}
							/>
							{popupEditableStores.length ? (
								<Box
									sx={{
										border: "1px solid",
										borderColor: "divider",
										borderRadius: 1.5,
										flex: 1,
										minHeight: 0,
										bgcolor: "#fff",
										display: "flex",
										flexDirection: "column",
									}}>
									<Box
										sx={{
											display: "grid",
											gridTemplateColumns: storeDetailsGridTemplate,
											bgcolor: "rgba(15,23,42,0.05)",
											borderBottom: "1px solid",
											borderColor: "divider",
											minWidth: storeDetailsColumnMinWidth,
											flexShrink: 0,
										}}>
										{storeDetailsHeaders.map((header) => (
											<Box
												key={header}
												sx={{
													px: 0.75,
													py: 0.5,
													fontSize: 12,
													fontWeight: 700,
													color: "text.secondary",
													minHeight: 42,
													display: "flex",
													alignItems: "center",
													justifyContent: "flex-start",
													textAlign: "left",
													bgcolor: "rgba(15,23,42,0.05)",
													borderRight: "1px solid rgba(226,232,240,0.7)",
												}}>
												{header}
											</Box>
										))}
									</Box>
									<Box
										sx={{
											overflow: "auto",
											flex: 1,
											minHeight: 0,
											borderBottom: "1px solid",
											borderColor: "divider",
										}}>
										{filteredStoreDetailsRows.map((store, index) => (
											<Box
												key={`${getStoreKey(store)}-${index}`}
												sx={{
													display: "grid",
													gridTemplateColumns: storeDetailsGridTemplate,
													minWidth: storeDetailsColumnMinWidth,
													borderBottom: index === filteredStoreDetailsRows.length - 1 ? "none" : "1px solid",
													borderColor: "divider",
													alignItems: "stretch",
													backgroundColor: index % 2 === 0 ? "#fff" : "rgba(248,250,252,0.7)",
												}}>
												<Box sx={{ px: 0.75, py: 0.25, minHeight: 46, display: "flex", alignItems: "center", borderRight: "1px solid rgba(226,232,240,0.7)" }}>
													<Typography variant="body2" fontWeight={600} sx={{ fontSize: 12.5, lineHeight: 1.2 }}>
														{getOptionLabel(store)}
													</Typography>
												</Box>
												<Box sx={{ px: 0.75, py: 0.25, minHeight: 46, display: "flex", alignItems: "center", borderRight: "1px solid rgba(226,232,240,0.7)" }}>
													<Typography variant="body2" color="text.secondary" sx={{ fontSize: 12 }}>
														{store?.store_code || store?.code || store?.admsite_code || "-"}
													</Typography>
												</Box>
												<Box sx={{ px: 0.75, py: 0.25, minHeight: 46, display: "flex", alignItems: "center", borderRight: "1px solid rgba(226,232,240,0.7)" }}>
													<Typography variant="body2" color="text.secondary" sx={{ fontSize: 12 }}>
														{store?.admsite_code || "-"}
													</Typography>
												</Box>
												<Box sx={{ px: 0.75, py: 0.25, minHeight: 46, display: "flex", alignItems: "center", borderRight: "1px solid rgba(226,232,240,0.7)" }}>
													<TextField
														size="small"
														fullWidth
														type="date"
														value={store?.opening_date || ""}
														onChange={(event) => handleStoreFieldChange(store, "opening_date", event.target.value)}
														InputLabelProps={{ shrink: true }}
														sx={{ "& .MuiInputBase-root": { height: 32, alignItems: "center" }, "& .MuiInputBase-input": { fontSize: 12, py: 0.5 } }}
													/>
												</Box>
												<Box sx={{ px: 0.75, py: 0.25, minHeight: 46, display: "flex", alignItems: "center", borderRight: "1px solid rgba(226,232,240,0.7)" }}>
													<TextField
														size="small"
														fullWidth
														type="number"
														placeholder="Deposit"
														value={store?.deposit_amount || ""}
														onChange={(event) => handleStoreFieldChange(store, "deposit_amount", event.target.value)}
														sx={{ "& .MuiInputBase-root": { height: 32, alignItems: "center" }, "& .MuiInputBase-input": { fontSize: 12, py: 0.5 } }}
													/>
												</Box>
												<Box sx={{ px: 0.75, py: 0.25, minHeight: 46, display: "flex", alignItems: "center", borderRight: "1px solid rgba(226,232,240,0.7)" }}>
													<TextField
														size="small"
														fullWidth
														type="number"
														placeholder="Capex"
														value={store?.capex_amount || ""}
														onChange={(event) => handleStoreFieldChange(store, "capex_amount", event.target.value)}
														sx={{ "& .MuiInputBase-root": { height: 32, alignItems: "center" }, "& .MuiInputBase-input": { fontSize: 12, py: 0.5 } }}
													/>
												</Box>
												<Box sx={{ px: 0.75, py: 0.25, minHeight: 46, display: "flex", alignItems: "center" }}>
													<TextField
														size="small"
														fullWidth
														placeholder="Area"
														value={store?.area || ""}
														onChange={(event) => handleStoreFieldChange(store, "area", event.target.value)}
														sx={{ "& .MuiInputBase-root": { height: 32, alignItems: "center" }, "& .MuiInputBase-input": { fontSize: 12, py: 0.5 } }}
													/>
												</Box>
											</Box>
										))}
										{filteredStoreDetailsRows.length === 0 ? (
											<Box sx={{ minWidth: storeDetailsColumnMinWidth, px: 1, py: 2 }}>
												<Typography variant="body2" color="text.secondary">
													No stores match the current filters.
												</Typography>
											</Box>
										) : null}
									</Box>
								</Box>
							) : (
								<Alert severity="info">No editable stores available. Disabled stores are hidden from this popup.</Alert>
							)}
						</Stack>
					</DialogContent>
					<DialogActions>
						<Button onClick={() => setDetailsDialogOpen(false)} disabled={saving}>
							Cancel
						</Button>
						<Button variant="contained" onClick={handleConfirmSave} disabled={saving || !selectedStores.length}>
							{saving ? "Saving..." : "Save Mapping"}
						</Button>
					</DialogActions>
				</Dialog>

				<Dialog
					open={Boolean(disableDialogStore)}
					onClose={saving ? undefined : () => {
						setDisableDialogStore(null);
						setDisableClosingDate("");
						setDisableDialogError("");
					}}
					fullWidth
					maxWidth="xs">
					<DialogTitle>Disable Store</DialogTitle>
					<DialogContent dividers>
						<Stack spacing={1.25} sx={{ pt: 0.5 }}>
							<Typography variant="body2" color="text.secondary">
								Add a closing date before disabling this store.
							</Typography>
							{disableDialogStore ? (
								<Typography variant="body2" fontWeight={700}>
									{getOptionLabel(disableDialogStore)}
								</Typography>
							) : null}
							<TextField
								size="small"
								label="Closing Date"
								type="date"
								value={disableClosingDate}
								onChange={(event) => setDisableClosingDate(event.target.value)}
								InputLabelProps={{ shrink: true }}
								fullWidth
							/>
							{disableDialogError ? <Alert severity="error">{disableDialogError}</Alert> : null}
						</Stack>
					</DialogContent>
					<DialogActions>
						<Button
							onClick={() => {
								setDisableDialogStore(null);
								setDisableClosingDate("");
								setDisableDialogError("");
							}}
							disabled={saving}>
							Cancel
						</Button>
						<Button variant="contained" onClick={handleConfirmDisableStore} disabled={saving}>
							Disable Store
						</Button>
					</DialogActions>
				</Dialog>

				<Dialog
					open={storesDialogOpen}
					onClose={storesTableLoading ? undefined : () => setStoresDialogOpen(false)}
					fullWidth
					maxWidth="xl"
					PaperProps={{
						sx: {
							height: "80vh",
							maxHeight: "80vh",
						},
					}}>
					<DialogTitle>COFO Stores</DialogTitle>
					<DialogContent
						dividers
						sx={{
							display: "flex",
							flexDirection: "column",
							overflow: "hidden",
						}}>
						<Box sx={{ display: "flex", justifyContent: "flex-end", mb: 1 }}>
							<Button
								variant="outlined"
								size="small"
								startIcon={<FileDownloadIcon />}
								onClick={handleExportStoresTable}
								disabled={storesTableLoading || !storesTableRows.length}>
								Export
							</Button>
						</Box>
						{storesTableLoading ? (
							<Box sx={{ py: 4, textAlign: "center" }}>
								<Typography variant="body2" color="text.secondary">
									Loading stores...
								</Typography>
							</Box>
						) : storesTableRows.length ? (
							<Box
								sx={{
									flex: 1,
									minHeight: 0,
									border: "1px solid",
									borderColor: "divider",
									borderRadius: 1.5,
									overflow: "hidden",
								}}>
								<DataGrid
									rows={storesTableGridRows}
									columns={storesTableGridColumns}
									disableRowSelectionOnClick
									headerHeight={38}
									rowHeight={34}
									pageSizeOptions={[25, 50, 100]}
									initialState={{
										pagination: {
											paginationModel: { pageSize: 25, page: 0 },
										},
									}}
									sx={{
										border: "none",
										fontSize: 11.5,
										"& .MuiDataGrid-columnHeaders": {
											backgroundColor: "rgba(15,23,42,0.05)",
										},
										"& .MuiDataGrid-columnHeaderTitleContainer": {
											px: 0.75,
										},
										"& .MuiDataGrid-columnHeaderTitle": {
											fontSize: 11.5,
											fontWeight: 700,
										},
										"& .MuiDataGrid-cell": {
											px: 0.75,
										},
										"& .MuiDataGrid-cellContent": {
											fontSize: 11.5,
											whiteSpace: "nowrap",
											overflow: "hidden",
											textOverflow: "ellipsis",
											textAlign: "left",
										},
										"& .MuiTablePagination-root": {
											fontSize: 10.5,
										},
										"& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows": {
											margin: 0,
											fontSize: 10.5,
										},
										"& .MuiTablePagination-select": {
											fontSize: 10.5,
										},
									}}
								/>
							</Box>
						) : (
							<Box sx={{ py: 4, textAlign: "center" }}>
								<Typography variant="body2" color="text.secondary">
									No store data available.
								</Typography>
							</Box>
						)}
					</DialogContent>
					<DialogActions>
						<Button onClick={() => setStoresDialogOpen(false)} disabled={storesTableLoading}>
							Close
						</Button>
					</DialogActions>
				</Dialog>
			</Stack>
		</Paper>
	);
};

export default MappingStores;
